/**
 * CritiqueCard Component
 *
 * Displays a single critique comment with user info, timestamp, rating, and content.
 * Used by ClassicFeedTemplate and SxSFeedTemplate.
 *
 * @module CritiqueCard
 */

import { useState } from 'react';
import type { Critique } from '../../../types/critique';
import type { Photo } from '../../../types/photo';
import type { MarkupEditData } from '../../../types/markup.types';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import { MarkupPreviewModal } from '../../modals/MarkupPreviewModal';
import { formatRelativeTime } from '../../../utils/time';
import logger from '../../../utils/logger';

export interface CritiqueCardProps {
  /**
   * Critique data to display
   */
  critique: Critique;

  /**
   * Photo data for shape reference interaction (050-critique-markup-integration)
   */
  photo?: Photo;

  /**
   * Expanded layout mode (for SxS feed)
   * When true, uses more vertical space for better readability
   */
  expanded?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * T028: Callback when critique card is clicked (040-critique-url-state)
   * Called with photoId and critiqueId for navigation
   */
  onCritiqueClick?: (photoId: string, critiqueId: string) => void;
}

/**
 * Renders star rating as string
 * Example: ★★★★☆ (4 out of 5 stars)
 */
function renderStars(rating: number): string {
  const fullStars = Math.floor(rating);
  const emptyStars = 5 - fullStars;
  return '★'.repeat(fullStars) + '☆'.repeat(emptyStars);
}

/**
 * CritiqueCard - Displays a single critique comment
 *
 * Compact mode (default): Horizontal layout, user • time | comment | ★★★★☆
 * Expanded mode: Vertical layout, more spacing for readability
 *
 * Feature 050: Interactive shape references with hover tooltips and click behavior
 */
export function CritiqueCard({
  critique,
  photo,
  expanded = false,
  className = '',
  onCritiqueClick,
}: CritiqueCardProps) {
  const displayName = critique.displayName || critique.username || 'Anonymous';
  const relativeTime = formatRelativeTime(critique.createdAt);

  // Feature 050: Markup preview modal state
  const [selectedMarkupIndex, setSelectedMarkupIndex] = useState<number | null>(null);

  // T029: Handle click to navigate to photo and critique (040-critique-url-state)
  const handleClick = () => {
    if (onCritiqueClick) {
      onCritiqueClick(critique.photoId, critique.id);
    }
  };

  // Feature 050: Handle shape reference click - open preview modal for specific shape
  const handleShapeReferenceClick = (shapeNumber: number) => {
    if (!critique.markup || critique.markup.length === 0) return;

    // Find the markup that contains the referenced shape
    const markupIndex = critique.markup.findIndex((m) => {
      // Type guard: check if editData has shapes property (markup edit)
      const editData = m.editData as unknown as MarkupEditData;
      const shapes = editData?.shapes || [];
      return shapes.some((s) => s.number === shapeNumber);
    });

    if (markupIndex >= 0) {
      logger.info('CritiqueCard', 'Opening markup preview for shape:', shapeNumber);
      setSelectedMarkupIndex(markupIndex);
    }
  };

  if (expanded) {
    // Expanded layout for SxS feed (vertical stack)
    return (
      <div
        onClick={handleClick}
        className={`rounded-lg border border-border bg-surface-2 p-3 space-y-2 ${
          onCritiqueClick ? 'cursor-pointer hover:bg-surface-3 transition-colors' : ''
        } ${className}`}
      >
        <div>
          {/* User info row */}
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">{displayName}</span>
            <span className="text-muted">{relativeTime}</span>
          </div>
          {/* Rating (if present) */}
          {critique.rating !== undefined && (
            <div className="text-yellow-500 text-sm" aria-label={`Rating: ${critique.rating} out of 5 stars`}>
              {renderStars(critique.rating)}
            </div>
          )}
        </div>

        {/* Comment content - Feature 050: MarkdownRenderer for interactive shape references */}
        {/* Use CSS line-clamp for visual truncation without breaking HTML */}
        <div className="line-clamp-3">
          <MarkdownRenderer
            content={critique.content}
            className="text-foreground leading-relaxed break-words"
            photo={photo}
            markup={critique.markup}
            onShapeReferenceClick={handleShapeReferenceClick}
          />
        </div>

      </div>
    );
  }

  // Compact layout for Classic feed (horizontal)
  return (
    <div
      onClick={handleClick}
      className={`rounded border border-border bg-surface-2 p-2 items-start gap-2 ${
        onCritiqueClick ? 'cursor-pointer hover:bg-surface-3 transition-colors' : ''
      } ${className}`}
    >
      <div className='flex'>
        {/* User */}
        <div className="flex-shrink-0 text-sm text-muted whitespace-nowrap">
          <span className="font-medium text-foreground">{displayName}</span>
          <span className="mx-1">•</span>
        </div>
        {/* Rating (if present) */}
        {critique.rating !== undefined && (
          <div className="flex-shrink-0 text-yellow-500 text-sm" aria-label={`Rating: ${critique.rating} out of 5 stars`}>
            {renderStars(critique.rating)}
          </div>
        )}
      </div>

      <div>
        {/* Comment content - Feature 050: MarkdownRenderer for interactive shape references */}
        {/* Use CSS line-clamp for visual truncation without breaking HTML */}
        <div className="line-clamp-3">
          <MarkdownRenderer
            content={critique.content}
            className="text-foreground text-sm leading-snug break-words"
            photo={photo}
            markup={critique.markup}
            onShapeReferenceClick={handleShapeReferenceClick}
          />
        </div>
      </div>

      {/* Time */}
      <div className="text-sm text-muted whitespace-nowrap">
        <span>{relativeTime}</span>
      </div>

      {/* Feature 050: Markup Preview Modal */}
      {photo && selectedMarkupIndex !== null && critique.markup && critique.markup[selectedMarkupIndex] && (
        <MarkupPreviewModal
          isOpen={true}
          onClose={() => {
            logger.info('CritiqueCard', 'Closing markup preview modal');
            setSelectedMarkupIndex(null);
          }}
          photo={photo}
          shapes={(critique.markup[selectedMarkupIndex].editData as unknown as MarkupEditData)?.shapes || []}
        />
      )}
    </div>
  );
}
